import React, {useLayoutEffect,} from "react";
import {Box, Chip, List, ListItem, Tooltip, useTheme} from "@mui/material";
import type {MaterialHarmonizationAdditionalInfo} from "../../../../services/ApiTypes.ts";
import type {DeserializedGridCellParams} from "../../../../components/muix-data-grid/MuiXColumnDefinitions.ts";
import {JsonPropertiesTagLayout} from "./JsonPropertiesTagLayout.tsx";

const chipSx = {
    flexShrink: 0,
    color: "#4088F8",
    border: "1px solid currentColor",
} as const;

const rowSx = {
    display: "flex",
    flexWrap: "nowrap",
    gap: 1,
    overflow: "hidden",
    minWidth: 0,
    width: "100%",
    alignItems: "center",
} as const;

export const JsonPropertiesCell: React.FC<DeserializedGridCellParams> = ({
                                                                             value,
                                                                         }) => {
    const theme = useTheme();
    const tags = (
        Array.isArray(value) ? value : []
    ) as MaterialHarmonizationAdditionalInfo[];
    const tagsRef = React.useRef<MaterialHarmonizationAdditionalInfo[]>(tags);

    const rowRef = React.useRef<HTMLDivElement | null>(null);
    const [visibleCount, setVisibleCount] = React.useState(() => tags.length);

    const tagLayout = React.useMemo(
        () => new JsonPropertiesTagLayout(theme),
        [theme],
    );
    const valueKey = React.useMemo(
        () => tagLayout.tagsContentKey(value),
        [tagLayout, value],
    );
    const syncedValueKeyRef = React.useRef<string | null>(null);

    // valueKey mirrors props.value content; avoids rerunning when only the array reference changes.
    useLayoutEffect(() => {
        const tagList = (
            Array.isArray(value) ? value : []
        ) as MaterialHarmonizationAdditionalInfo[];
        tagsRef.current = tagList;
        const tagContainer = rowRef.current;
        if (!tagContainer) return;

        if (!Array.isArray(tagList) || tagList.length === 0) {
            syncedValueKeyRef.current = valueKey;
            setVisibleCount(0);
            return;
        }

        if (syncedValueKeyRef.current !== valueKey) {
            syncedValueKeyRef.current = valueKey;
            setVisibleCount(
                tagLayout.computeInitialVisibleCount(tagContainer, tagList),
            );
            return;
        }

        if (
            tagLayout.shouldDecrementVisibleCount(
                tagContainer,
                tagList,
                visibleCount,
            )
        ) {
            setVisibleCount((count) => count - 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- valueKey is derived from props.value
    }, [tagLayout, valueKey, visibleCount]);

    // ResizeObserver must stay on the live row node: a single persistent Box (below) keeps ref stable when
    // visibleCount toggles; the callback uses rowRef.current so column grow/shrink always recalculates.
    useLayoutEffect(() => {
        const tagContainer = rowRef.current;
        if (!tagContainer || typeof ResizeObserver === "undefined") return;

        const ro = new ResizeObserver(() => {
            const node = rowRef.current;
            if (!node) return;
            setVisibleCount(
                tagLayout.computeInitialVisibleCount(node, tagsRef.current),
            );
        });
        ro.observe(tagContainer);
        return () => ro.disconnect();
    }, [tagLayout, valueKey]);

    const hiddenCount = tags.length - visibleCount;
    const hiddenTags = hiddenCount > 0 ? tags.slice(visibleCount) : [];

    if (!Array.isArray(tags) || tags.length === 0) return null;

    return (
        <Box ref={rowRef} sx={rowSx}>
            {visibleCount > 0 &&
                tags
                    .slice(0, visibleCount)
                    .map((t) => (
                        <Chip
                            size="small"
                            label={tagLayout.formatPropertyLabel(t)}
                            sx={chipSx}
                        />
                    ))}
            {hiddenCount > 0 && (
                <Tooltip
                    arrow
                    title={
                        <List component="ul" sx={{px: '2px', py: 0}}>
                            {hiddenTags.map((t) => (
                                <ListItem key={t.id} sx={{p: 0}}>
                                    {tagLayout.formatPropertyLabel(t)}
                                </ListItem>
                            ))}
                        </List>
                    }
                >
                    <Chip
                        size="small"
                        variant="outlined"
                        label={tagLayout.overflowLabel(hiddenCount)}
                        sx={{flexShrink: 0}}
                    />
                </Tooltip>
            )}
        </Box>
    );
};
